import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions
} from "@tanstack/react-query";

import { AnalysisConfiguration } from "@/components/analysisSummary/filterByGrid/types";
import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import {
  AggregationCalculationType,
  GetAnalysisResponse,
  GetAnalyticResponse,
  SaveAnalysisUserDatasetPayload,
  SaveAnalysisUserDatasetReponse
} from "@/types/analysis";
import {
  AnalysisDatasetSchema,
  AnalysisDatasetSummary,
  GeneralSummary
} from "@/types/analysisSummary";
import { ExportDatasetFileType } from "@/types/dataset";
import {
  GetAnalysisTemplateDetail,
  GetAnalysisTemplatePayload,
  GetAnalysisTemplateResponse
} from "@/types/template";
import { CustomError } from "@/utils/errors";

import {
  cancelAnalysisJob,
  exportAnalysisSummary,
  getAnalysis,
  getAnalysisStatus,
  getAnalysisSummary,
  getAnalysisSummaryByDataset,
  getAnalysisTemplate,
  getAnalysisTemplateDetail,
  getSchemaDatasetByAnalysis,
  rerunAnalysisJob,
  saveAnalysisOutputAsDataset
} from "./analysisApi";

export const GET_ANALYSIS_TEMPLATES_DEFAULT_PER_PAGE = 10;

export const GET_ANALYSIS_TEMPLATES_DEFAULT_PARAMS = {
  page: 1,
  per_page: GET_ANALYSIS_TEMPLATES_DEFAULT_PER_PAGE
};

export const useSaveAnalysisUserDatasetMutation = (
  options?: UseMutationOptions<
    SaveAnalysisUserDatasetReponse,
    {
      code: number;
      message: CustomError<
        [
          { field: string; message: string },
          { field: "available_kb"; message: string },
          { field: "required_kb"; message: string }
        ]
      >;
    },
    SaveAnalysisUserDatasetPayload,
    unknown
  >
) => {
  return useMutation({
    mutationFn: async (payload: SaveAnalysisUserDatasetPayload) => {
      return saveAnalysisOutputAsDataset(payload);
    },
    ...options
  });
};

export const useGetAnalysisSummary = (
  id: string,
  options?: Omit<
    UseQueryOptions<GeneralSummary, Error, GeneralSummary>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["analysis-summary", id],
    queryFn: () => getAnalysisSummary(id),
    ...options
  });
};

export const useGetAnalysisStatus = (
  analysisId: string,
  options?: Omit<
    UseQueryOptions<GetAnalysisResponse, Error, GetAnalysisResponse>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["analysis-status", analysisId],
    queryFn: () => getAnalysisStatus(analysisId),
    ...options
  });
};

export const useGetAnalysis = (
  analysisId: string,
  options?: Omit<UseMutationOptions<GetAnalyticResponse, Error>, "mutationFn">
) => {
  return useMutation({
    mutationFn: async () => getAnalysis(analysisId),
    ...options
  });
};

export const useGetSchemaDatasetByAnalysis = (
  analysisId: string | undefined,
  datasetId: string,
  options?: Omit<
    UseQueryOptions<
      AnalysisDatasetSchema,
      { message: CustomError },
      AnalysisDatasetSchema
    >,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: [`analysis-summary-${datasetId}`, datasetId, analysisId],
    queryFn: () => getSchemaDatasetByAnalysis(analysisId, datasetId),
    enabled: !!analysisId && !!datasetId,
    staleTime: 0,
    ...options
  });
};

export const useExportAnalysisSummary = (
  options?: Omit<
    UseMutationOptions<
      { operation_id: string },
      Error,
      { analysisId: string; fileType: ExportDatasetFileType }
    >,
    "mutationFn"
  >
) => {
  return useMutation({
    mutationFn: async (payload: {
      analysisId: string;
      fileType: ExportDatasetFileType;
    }) => {
      return exportAnalysisSummary(payload.analysisId, payload.fileType);
    },
    ...options
  });
};
export const useGetAnalysisSummaryByDataset = (
  datasetId: string,
  analysisId: string | undefined,
  analysisConfig: AnalysisConfiguration | null,
  grids: string[] = [],
  options?: Omit<
    UseQueryOptions<
      AnalysisDatasetSummary,
      { message: CustomError },
      AnalysisDatasetSummary
    >,
    "queryKey" | "queryFn"
  >
) => {
  /**
   * Determines if the fetch operation can proceed.
   * Requirements:
   * - analysisId and datasetId must be truthy
   * - analysisConfig must be non-null
   * - A method must be selected in analysisConfig
   */
  const canFetch =
    !!analysisId &&
    !!datasetId &&
    analysisConfig !== null &&
    !!analysisConfig?.method?.value;
  return useQuery({
    queryKey: [{ datasetId, analysisId, analysisConfig, grids }],
    queryFn: () =>
      getAnalysisSummaryByDataset({
        datasetId,
        analysisId,
        method: analysisConfig?.method?.value,
        attribute: analysisConfig?.attribute_x?.value,
        calculation: analysisConfig?.calculationMethod
          ?.value as AggregationCalculationType,
        category: analysisConfig?.category?.value,
        filters:
          analysisConfig?.filterBy?.[0] === DROPDOWN_ALL_VALUE
            ? null
            : analysisConfig?.filterBy, // Optional: filter by attributes
        sort_type: analysisConfig?.sort?.value ?? "ASC", // Optional: sort direction
        sort_by: analysisConfig?.sortBy?.value, // Optional: field name to sort by
        grid: grids?.[0] === DROPDOWN_ALL_VALUE ? null : grids,
        attribute_y: analysisConfig?.attribute_y?.value
      }),
    enabled: canFetch,
    // Since the API response isn’t sorted, which causes inconsistency, we removed the staleTime and gcTime
    staleTime: Infinity,
    gcTime: Infinity,
    ...options
  });
};

export const useGetInfiniteAnalysisTemplates = (
  {
    per_page,
    page
  }: GetAnalysisTemplatePayload = GET_ANALYSIS_TEMPLATES_DEFAULT_PARAMS,
  options?: Partial<
    UseInfiniteQueryOptions<
      GetAnalysisTemplateResponse,
      Error,
      InfiniteData<GetAnalysisTemplateResponse>
    >
  >
) =>
  useInfiniteQuery({
    queryKey: ["getInfiniteAnalysisTemplates", page],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      getAnalysisTemplate({
        page: pageParam as number,
        per_page
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.data?.length < GET_ANALYSIS_TEMPLATES_DEFAULT_PER_PAGE)
        return undefined;
      return allPages.length + 1;
    },
    ...options
  });

export const useGetAnalysisTemplateDetail = (
  templateId: string,
  options?: Omit<
    UseQueryOptions<
      GetAnalysisTemplateDetail,
      Error,
      GetAnalysisTemplateDetail
    >,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["analysis-template-detail", templateId],
    queryFn: () => getAnalysisTemplateDetail(templateId),
    ...options
  });
};

export const useCancelAnalysisJob = (
  options?: Omit<
    UseMutationOptions<
      { id: string; status: string },
      Error,
      { analysisId: string }
    >,
    "mutationFn"
  >
) => {
  return useMutation({
    mutationFn: async (payload: { analysisId: string }) => {
      return cancelAnalysisJob(payload.analysisId);
    },
    ...options
  });
};

export const useRerunAnalysisJob = (
  options?: Omit<
    UseMutationOptions<
      { id: string; status: string; message?: string },
      Error,
      { analysisId: string }
    >,
    "mutationFn"
  >
) => {
  return useMutation({
    mutationFn: async (payload: { analysisId: string }) => {
      return rerunAnalysisJob(payload.analysisId);
    },
    ...options
  });
};
